import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const ReportModal = ({ contentType, contentId, onClose }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { value: 'spam', label: 'Spam ou contenu indésirable' },
    { value: 'inappropriate', label: 'Contenu inapproprié ou offensant' },
    { value: 'violence', label: 'Violence ou contenu dangereux' },
    { value: 'harassment', label: 'Harcèlement ou intimidation' },
    { value: 'misinformation', label: 'Fausses informations' },
    { value: 'copyright', label: 'Violation de droits d\'auteur' },
    { value: 'other', label: 'Autre' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      toast.error('Veuillez sélectionner une raison');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post('/api/admin/reports', {
        content_type: contentType,
        content_id: contentId,
        reason: reason,
        details: details || undefined
      });
      
      console.log('Report submitted successfully:', response.data);
      toast.success('Signalement envoyé avec succès');
      
      // Close modal after a short delay to ensure toast is visible
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('Report error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors du signalement';
      toast.error(errorMessage);
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-start justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
      style={{ paddingTop: '80px' }}
      onClick={(e) => {
        // Close modal if clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="relative w-full max-w-lg bg-gradient-to-br from-gray-900 to-black border border-white/20 rounded-2xl shadow-2xl mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-500/20 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">Signaler ce contenu</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Reason Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Motif du signalement *
            </label>
            <div className="space-y-2">
              {reasons.map((r) => (
                <label
                  key={r.value}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-white/10 hover:border-magenta-500/50 hover:bg-white/5 transition-all cursor-pointer"
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-4 h-4 text-magenta-500 focus:ring-magenta-500 focus:ring-2"
                  />
                  <span className="text-white">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Details */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Détails supplémentaires (optionnel)
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Décrivez le problème..."
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500 focus:ring-2 focus:ring-magenta-500/20 transition-all resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg font-medium text-white transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !reason}
              className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Envoi...' : 'Envoyer le signalement'}
            </button>
          </div>
        </form>

        {/* Warning Notice */}
        <div className="px-6 pb-6">
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-sm text-yellow-200">
              <strong>Note :</strong> Les signalements abusifs peuvent entraîner des sanctions sur votre compte.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
